import { useState } from 'react';
import { Box } from '@coolblue-development/becky';
import { Loader, Typography } from '@coolblue-development/freddy';
import { datadogLogs } from '@datadog/browser-logs';
import { Paperclip, Download } from '@coolblue-development/icons';
import { EmailAttachment } from '../../../../../models/Email';
import { fetchAttachment } from '../../../../../services/fetchAttachment';

interface EmailAttachmentChipsProps {
  attachments: EmailAttachment[];
  contactId: string;
  onDownloadError?: () => void;
}

const EmailAttachmentChips = ({
  attachments,
  contactId,
  onDownloadError
}: EmailAttachmentChipsProps): JSX.Element | null => {
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  if (attachments.length === 0) return null;

  const handleDownload = async (attachment: EmailAttachment): Promise<void> => {
    setLoadingIds(prev => new Set(prev).add(attachment.fileId));
    try {
      const { url } = await fetchAttachment(attachment.fileId, contactId);
      window.open(url, '_blank');
    } catch (error) {
      datadogLogs.logger.error('Failed to download attachment', { channel: 'email' }, error as Error);
      onDownloadError?.();
    } finally {
      setLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(attachment.fileId);
        return next;
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px', marginBottom: '12px' }}>
      {attachments.map(attachment => {
        const isLoading = loadingIds.has(attachment.fileId);
        return (
          <Box
            key={attachment.fileId}
            onClick={isLoading ? undefined : () => void handleDownload(attachment)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minWidth: '250px',
              minHeight: '40px',
              padding: '6px 12px',
              border: '1px solid #DDDDDD',
              borderRadius: '4px',
              backgroundColor: '#FFFFFF',
              cursor: isLoading ? 'default' : 'pointer',
              gap: '8px',
              '&:hover': { backgroundColor: isLoading ? '#FFFFFF' : '#F2F7FC' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
              <Paperclip color="#868A90" />
              <Box
                component="span"
                sx={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                <Typography color="#666666">{attachment.fileName}</Typography>
              </Box>
            </Box>

            {isLoading ? (
              <Box
                sx={{
                  position: 'relative',
                  marginRight: '8px'
                }}
              >
                <Loader size="small" />
              </Box>
            ) : (
              <Download color="#0090E3" />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default EmailAttachmentChips;
